import { useEffect } from "react";
import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Empty,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  MinusCircleFilled,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useGetPreflightQuery } from "../../../redux/api/scheduling";
import type {
  PreflightCategory,
  PreflightCheck,
  PreflightStatus,
  SchedulerEngine,
} from "../../../interfaces/Scheduling.interface";

const { Panel } = Collapse;
const { Text } = Typography;

const STATUS_COLOR: Record<PreflightStatus, string> = {
  pass: "green",
  fail: "red",
  warn: "orange",
  skip: "default",
};

const STATUS_LABEL: Record<PreflightStatus, string> = {
  pass: "PASS",
  fail: "FAIL",
  warn: "WARN",
  skip: "SKIP",
};

const ENGINE_LABEL: Record<SchedulerEngine, string> = {
  heuristic: "Heuristic",
  cp_sat: "CP-SAT",
};

function StatusIcon({ status }: { status: PreflightStatus }) {
  switch (status) {
    case "pass":
      return <CheckCircleFilled style={{ color: "#52c41a" }} />;
    case "fail":
      return <CloseCircleFilled style={{ color: "#cf1322" }} />;
    case "warn":
      return <ExclamationCircleFilled style={{ color: "#faad14" }} />;
    case "skip":
    default:
      return <MinusCircleFilled style={{ color: "#bfbfbf" }} />;
  }
}

function relativeTime(iso?: string): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const secs = Math.max(0, Math.round(diffMs / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}

interface PreflightDrawerProps {
  open: boolean;
  engine: SchedulerEngine | null;
  onClose: () => void;
  onContinue: (engine: SchedulerEngine) => void;
  continueLoading?: boolean;
}

export default function PreflightDrawer({
  open,
  engine,
  onClose,
  onContinue,
  continueLoading,
}: PreflightDrawerProps) {
  const { data, isFetching, refetch } = useGetPreflightQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });

  // Refetch each time the drawer opens so we always show fresh data.
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  const title = engine ? `Pre-Flight Checks — ${ENGINE_LABEL[engine]}` : "Pre-Flight Checks";

  const categories: PreflightCategory[] = data?.categories ?? [];
  const defaultExpanded = categories
    .filter((c) => c.status === "fail" || c.status === "warn")
    .map((c) => c.key);

  return (
    <Drawer
      title={
        <Space>
          <ThunderboltOutlined />
          <span>{title}</span>
          {data?.checked_at && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              checked {relativeTime(data.checked_at)}
            </Text>
          )}
        </Space>
      }
      width={560}
      placement="right"
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Re-check
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={continueLoading}
            onClick={() => engine && onContinue(engine)}
            disabled={!engine}
          >
            Continue to Run
          </Button>
        </div>
      }
    >
      {isFetching && !data ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : !data ? (
        <Empty description="No preflight data" />
      ) : (
        <>
          {data.running_engine && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`The ${data.running_engine} engine is currently running`}
              description="Your run will queue behind it (the engine acquires an advisory lock on start)."
            />
          )}

          <Space size="large" wrap style={{ marginBottom: 16 }}>
            <Tag color="green">{data.summary.pass} passed</Tag>
            <Tag color="red">{data.summary.fail} failed</Tag>
            <Tag color="orange">{data.summary.warn} warnings</Tag>
            {data.summary.skip > 0 && <Tag>{data.summary.skip} skipped</Tag>}
          </Space>

          <Collapse defaultActiveKey={defaultExpanded}>
            {categories.map((cat) => (
              <Panel
                key={cat.key}
                header={
                  <Space>
                    <StatusIcon status={cat.status} />
                    <span style={{ fontWeight: 500 }}>{cat.name}</span>
                    <Tag color={STATUS_COLOR[cat.status]}>
                      {STATUS_LABEL[cat.status]}
                    </Tag>
                  </Space>
                }
              >
                <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
                  {cat.checks.map((check: PreflightCheck) => (
                    <li
                      key={check.key}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        padding: "6px 0",
                        borderBottom: "1px dashed #f0f0f0",
                      }}
                    >
                      <StatusIcon status={check.status} />
                      <div style={{ flex: 1 }}>
                        <div>{check.name}</div>
                        {check.details && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {check.details}
                          </Text>
                        )}
                      </div>
                      <Tag color={STATUS_COLOR[check.status]}>
                        {STATUS_LABEL[check.status]}
                      </Tag>
                    </li>
                  ))}
                </ul>
              </Panel>
            ))}
          </Collapse>
        </>
      )}
    </Drawer>
  );
}
